import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useAdminAuth } from "../services/admin/AdminAuthServices";


export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const { forgotPasswordAPI, verifyOtpAPI, loading: isLoading } = useAdminAuth();

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    const res = await forgotPasswordAPI(email);
    if (res.status === "success") {
      setStep(2);
      setCountdown(60);
    } else {
      setError(res.message || "Failed to send OTP. Please try again.");
    }
  };

  const handleResendOTP = async () => {
    setError("");
    const res = await forgotPasswordAPI(email);
    if (res.status === "success") {
      setCountdown(60);
    } else {
      setError(res.message || "Failed to resend OTP.");
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");

    if (!otp) {
      setError("Please enter the OTP");
      return;
    }

    const res = await verifyOtpAPI(email, otp);
    if (res.status === "success") {
      // Redirect to password reset page with email and verification token
      window.location.href = `/admin/reset-password?email=${encodeURIComponent(
        email,
      )}&token=${res.verification_token}`;
    } else {
      setError(res.message || "Invalid OTP. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#62397f]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md relative">
        {/* Back Button */}
        <Link
          to="/admin/login"
          className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] hover:underline mb-6 font-inter font-medium"
        >
          <ArrowLeft size={18} />
          Back to Login
        </Link>

        {/* Logo and Brand */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 bg-gradient-to-br from-[#62397f] to-[#7C3AED]rounded-2xl flex items-center justify-center shadow-xl mx-auto mb-4 transform hover:scale-105 transition-transform duration-300">
            <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" loading="eager" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
            Forgot Password
          </h1>
          <p className="text-[#666666] dark:text-[#A0A0A0] font-inter">
            {step === 1
              ? "Enter your email to receive an OTP"
              : "Enter the OTP sent to your email"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-6 md:p-8 transform hover:scale-[1.01] transition-transform duration-300">
          {/* Step 1: Email Input */}
          {step === 1 && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                  <p className="text-red-600 dark:text-red-400 text-sm font-inter">
                    {error}
                  </p>
                </div>
              )}

              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                  Email Address
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                    size={18}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] focus:border-transparent transition-all duration-200"
                    placeholder="admin@ststephens.com"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-poppins disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Sending OTP...
                  </span>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Input */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Success Message */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 animate-fade-in">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                    size={20}
                  />
                  <div>
                    <h3 className="text-green-800 dark:text-green-300 font-semibold mb-1 font-poppins">
                      OTP Sent!
                    </h3>
                    <p className="text-green-700 dark:text-green-400 text-sm font-inter">
                      We've sent a 6-digit code to{" "}
                      <span className="font-semibold">{email}</span>
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleVerifyOTP} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                    <p className="text-red-600 dark:text-red-400 text-sm font-inter">
                      {error}
                    </p>
                  </div>
                )}

                {/* OTP Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="w-full px-4 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white text-center text-2xl tracking-widest placeholder-[#999999] dark:placeholder-[#666666] font-mono focus:outline-none focus:ring-2 focus:ring-[#62397f] focus:border-transparent transition-all duration-200"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                {/* Resend Button */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || isLoading}
                    className="text-sm text-[#62397f] dark:text-[#A78BFA] hover:underline font-inter font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    {countdown > 0
                      ? `Resend OTP in ${countdown}s`
                      : "Resend OTP"}
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full py-3 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-poppins disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    "Verify OTP"
                  )}
                </button>

                {/* Change Email */}
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp("");
                    setError("");
                  }}
                  className="w-full text-sm text-[#666666] dark:text-[#A0A0A0] hover:text-[#111111] dark:hover:text-white font-inter"
                >
                  Change email address
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[#666666] dark:text-[#A0A0A0] mt-6 font-inter">
          &copy; {new Date().getFullYear()} St. Stephen's Rentals & Events.<br />All rights reserved.
        </p>
      </div>

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}
